import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { formatUTCDateToLocal, isPastTime } from '../../utils/timezone';
import './UpdateLiabilityWaiverPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

interface UserData {
  firstName: string;
  lastName: string;
  liabilityWaiverAccepted: boolean;
  liabilityWaiverAcceptedAt?: string;
  liabilityWaiverExpiresAt?: string;
}

const UpdateLiabilityWaiverPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const passcode = location.state?.passcode || localStorage.getItem('currentPasscode') || '';
  
  const [userData, setUserData] = useState<UserData | null>(null);
  const [formData, setFormData] = useState({
    liabilityWaiverAccepted: false
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!passcode) {
      navigate('/');
      return;
    }
    fetchUserData();
  }, [passcode, navigate]);

  const fetchUserData = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/user-liability-waiver/${passcode}`);
      if (response.ok) {
        const data = await response.json();
        setUserData(data);
        setFormData({
          liabilityWaiverAccepted: false
        });
      } else {
        setMessage('Failed to load user data');
      }
    } catch (error) {
      setMessage('Failed to connect to server');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.liabilityWaiverAccepted) {
      setMessage('You must accept the liability waiver to continue');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/update-liability-waiver/${passcode}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setMessage('Liability waiver updated successfully!');
        // Refresh user data to get updated expiration date
        await fetchUserData();
        // Redirect to success page after a short delay
        setTimeout(() => {
          navigate('/success', { state: { passcode } });
        }, 2000);
      } else {
        const errorData = await response.json();
        setMessage(errorData.message || 'Failed to update liability waiver');
      }
    } catch (error) {
      setMessage('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/success', { state: { passcode } });
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return formatUTCDateToLocal(dateString);
  };

  const isExpired = () => {
    if (!userData?.liabilityWaiverExpiresAt) return false;
    return isPastTime(userData.liabilityWaiverExpiresAt);
  };

  if (isLoading) {
    return (
      <div className="update-waiver-container">
        <div className="update-waiver-card">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading user data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="update-waiver-container">
      <div className="update-waiver-card">
        <div className="update-waiver-header">
          <div className="logo">
            <img src="/VolunteerHeart.png" alt="FabMo Volunteer" className="logo-image" />
          </div>
          <h1 className="update-waiver-title">Update Liability Waiver</h1>
          <p className="update-waiver-subtitle">
            {userData ? `${userData.firstName} ${userData.lastName}` : 'User'} - Update your liability waiver status
          </p>
        </div>

        <div className="update-waiver-content">
          {/* Current Status */}
          {userData && (
            <div className="current-status">
              <h3>Current Liability Waiver Status</h3>
              <div className="status-info">
                <div className="status-item">
                  <span className="status-label">Status:</span>
                  <span className={`status-value ${userData.liabilityWaiverAccepted && !isExpired() ? 'current' : userData.liabilityWaiverAccepted ? 'expired' : 'not-accepted'}`}>
                    {userData.liabilityWaiverAccepted && !isExpired() ? 'Current' : userData.liabilityWaiverAccepted ? 'Expired' : 'Not Accepted'}
                  </span>
                </div>
                {userData.liabilityWaiverAcceptedAt && (
                  <div className="status-item">
                    <span className="status-label">Accepted Date:</span>
                    <span className="status-value">{formatDate(userData.liabilityWaiverAcceptedAt)}</span>
                  </div>
                )}
                {userData.liabilityWaiverExpiresAt && (
                  <div className="status-item">
                    <span className="status-label">Expires Date:</span>
                    <span className={`status-value ${isExpired() ? 'expired' : ''}`}>
                      {formatDate(userData.liabilityWaiverExpiresAt)}
                      {isExpired() && ' (EXPIRED)'}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="waiver-form">
            <div className="form-divider">
              <div className="divider-line"></div>
              <span className="divider-text">Liability Waiver</span>
              <div className="divider-line"></div>
            </div>

            <div className="liability-waiver-section">
              <div className="waiver-content">
                <h3>FabMo Liability Waiver and Code of Conduct</h3>
                <div className="waiver-text">
                  <h4>1. Photography and Videography</h4>
                  <p>I understand that photography and videography are frequently conducted at FabMo for promotional and informational purposes. By allowing myself to be photographed, I understand that I am giving my permission for my image and/or name to be used, without compensation, for the purpose of promoting FabMo and its mission. If I do not wish to be photographed, I will inform the photographer.</p>

                  <h4>2. Liability Waiver</h4>
                  <p>I hereby waive and release all claims for damages that I may have against FabMo, its officers, agents or governing Board for all injuries and illnesses of every type, suffered by myself or any minors in my care that may happen while at FabMo. I understand that there are certain known and foreseeable risks of physical injury and illness associated with participation at FabMo. I understand that there may be other risks of which I may not be presently aware. I understand that I am free at any time to refuse to do anything at FabMo that I believe poses a hazard to me. By submitting this Release, I expressly assume all these risks for myself and/or any minors in my care, and will not make any claims against FabMo, its officers, agents or governing Board.</p>

                  <h4>3. Authorization for Medical Treatment</h4>
                  <p>In the event a medical emergency renders me unable to give directives or consent, I hereby give my permission to be taken to the nearest available medical facility and have any necessary emergency medical treatment administered. Furthermore, I specifically release all such medical care providers, FabMo and their representatives from any liability in acting in such circumstances.</p>

                  <h4>4. Code of Conduct</h4>
                  <ul>
                    <li>I understand and will promote a supportive community engaged in furthering FabMo's mission: FabMo promotes and encourages good stewardship of the earth by diverting discontinued designer fabrics, tiles, wallpapers and other similar materials from landfills, making them available to all interested persons and inspiring creative reuse.</li>
                    <li>I will treat all volunteers and community members with kindness, respect, courtesy and dignity, modelling behaviour that recognizes the human rights of all people and is fair, inclusive and respectful of the diversity among the FabMo community.</li>
                    <li>I will follow the rules and policies set forth by the FabMo Board and the instructions of team leaders and volunteer supervisors.</li>
                    <li>I will observe safe work habits and be aware of the safety of others.</li>
                  </ul>
                  <p>I hereby give my permission for FabMo to collect and store my personal information and to utilize it or share it with team members when necessary for my well being. FabMo promises not to sell or share your information to/with any individual or organization outside FabMo and to protect it from unauthorized use.</p>
                  <p>By signing my name below, I am acknowledging the receipt of the information in this form, and I have read, understood and am agreeing to the conditions and terms set out in this form.</p>
                </div>
              </div>

              <div className="waiver-agreement">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="liabilityWaiverAccepted"
                    checked={formData.liabilityWaiverAccepted}
                    onChange={handleInputChange}
                    required
                    className="waiver-checkbox"
                  />
                  <span className="checkbox-text">
                    I have read and agree to the FabMo Liability Waiver and Code of Conduct *
                  </span>
                </label>
              </div>
            </div>

            <button
              type="submit"
              className={`submit-button ${loading ? 'loading' : ''}`}
              disabled={loading}
            >
              {loading ? (
                <div className="loading-spinner"></div>
              ) : (
                'Update Liability Waiver'
              )}
            </button>

            {message && (
              <div className={`message ${message.includes('successfully') ? 'success' : 'error'}`}>
                {message}
              </div>
            )}
          </form>
        </div>

        <div className="update-waiver-footer">
          <button onClick={handleBack} className="back-button">
            ← Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpdateLiabilityWaiverPage;
